import React, { useState, useRef, useEffect } from 'react';
import client from '../api/client';
import { FiSend, FiPlus, FiTrash2, FiMessageSquare } from 'react-icons/fi';
import { formatDistanceToNow } from 'date-fns';

export default function ChatPage() {
  const [conversations, setConversations] = useState([]);
  const [activeConversationId, setActiveConversationId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [loadingConversations, setLoadingConversations] = useState(true);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => {
    loadConversations();
  }, []);

  const loadConversations = async () => {
    setLoadingConversations(true);
    try {
      const res = await client.get('/chat/conversations');
      setConversations(res.data);
    } catch {
      // silently fail
    } finally {
      setLoadingConversations(false);
    }
  };

  useEffect(() => {
    if (activeConversationId) {
      loadMessages(activeConversationId);
    } else {
      setMessages([]);
    }
  }, [activeConversationId]);

  const loadMessages = async (id) => {
    try {
      const res = await client.get(`/chat/conversations/${id}`);
      setMessages(res.data.messages || []);
    } catch {
      setMessages([]);
    }
  };

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const handleNewConversation = () => {
    setActiveConversationId(null);
    setMessages([]);
    inputRef.current?.focus();
  };

  const handleSelectConversation = (id) => {
    setActiveConversationId(id);
  };

  const handleDeleteConversation = async (e, id) => {
    e.stopPropagation();
    try {
      await client.delete(`/chat/conversations/${id}`);
      setConversations(prev => prev.filter(c => c.id !== id));
      if (activeConversationId === id) {
        setActiveConversationId(null);
        setMessages([]);
      }
    } catch {
      // silently fail
    }
  };

  const handleSend = async (e) => {
    e.preventDefault();
    const text = input.trim();
    if (!text || loading) return;

    setMessages(prev => [...prev, { role: 'user', content: text }]);
    setInput('');
    setLoading(true);

    try {
      const res = await client.post('/chat', {
        message: text,
        conversationId: activeConversationId,
      });

      if (!activeConversationId) {
        setActiveConversationId(res.data.conversationId);
      }

      setMessages(prev => [...prev, { role: 'assistant', content: res.data.reply }]);
      loadConversations();
    } catch {
      setMessages(prev => [...prev, {
        role: 'assistant',
        content: 'Sorry, something went wrong. Please try again.',
      }]);
    } finally {
      setLoading(false);
      inputRef.current?.focus();
    }
  };

  return (
    <div className="flex h-[calc(100vh-7rem)] gap-4">
      {/* Sidebar */}
      <div className="w-72 flex-shrink-0 flex flex-col glass rounded-2xl">
        <div className="p-3 border-b border-white/10">
          <button
            onClick={handleNewConversation}
            className="w-full flex items-center gap-2 px-3 py-2 bg-primary-600 text-white rounded-xl text-sm font-medium hover:bg-primary-700 transition"
          >
            <FiPlus size={16} />
            New conversation
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-2 space-y-1">
          {loadingConversations && conversations.length === 0 && (
            <div className="flex justify-center py-8">
              <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white" />
            </div>
          )}

          {!loadingConversations && conversations.length === 0 && (
            <p className="text-white/30 text-xs text-center py-8">No conversations yet</p>
          )}

          {conversations.map(conv => (
            <div
              key={conv.id}
              onClick={() => handleSelectConversation(conv.id)}
              className={`group flex items-center gap-2 px-3 py-2.5 rounded-xl cursor-pointer transition text-sm ${
                activeConversationId === conv.id
                  ? 'bg-white/20 text-white'
                  : 'text-white/70 hover:bg-white/10 hover:text-white'
              }`}
            >
              <FiMessageSquare size={14} className="flex-shrink-0" />
              <div className="flex-1 min-w-0">
                <p className="truncate">{conv.summary}</p>
                <p className="text-xs text-white/40 mt-0.5">
                  {formatDistanceToNow(new Date(conv.updatedAt + 'Z'), { addSuffix: true })}
                </p>
              </div>
              <button
                onClick={(e) => handleDeleteConversation(e, conv.id)}
                className="opacity-0 group-hover:opacity-100 text-white/30 hover:text-red-400 transition"
              >
                <FiTrash2 size={14} />
              </button>
            </div>
          ))}
        </div>
      </div>

      {/* Chat area */}
      <div className="flex-1 flex flex-col">
        <div className="mb-4">
          <h1 className="text-2xl font-bold text-white drop-shadow-sm">Chat</h1>
          <p className="text-white/70 mt-1">Ask questions about your health journey</p>
        </div>

        {/* Messages */}
        <div className="flex-1 overflow-y-auto glass rounded-2xl p-4 space-y-4 mb-4">
          {messages.length === 0 && (
            <div className="flex items-center justify-center h-full">
              <div className="text-center">
                <p className="text-white/40 text-lg">No messages yet</p>
                <p className="text-white/30 text-sm mt-1">
                  Ask about symptoms, diet tips, or your tracking patterns
                </p>
              </div>
            </div>
          )}

          {messages.map((msg, i) => (
            <div
              key={msg.id || i}
              className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}
            >
              <div
                className={`max-w-[80%] rounded-2xl px-4 py-3 text-sm leading-relaxed ${
                  msg.role === 'user'
                    ? 'bg-primary-600 text-white rounded-br-md'
                    : 'bg-white/10 text-white/90 rounded-bl-md'
                }`}
              >
                <p className="whitespace-pre-wrap">{msg.content}</p>
              </div>
            </div>
          ))}

          {loading && (
            <div className="flex justify-start">
              <div className="bg-white/10 rounded-2xl rounded-bl-md px-4 py-3">
                <div className="flex gap-1.5">
                  <span className="w-2 h-2 bg-white/40 rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
                  <span className="w-2 h-2 bg-white/40 rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
                  <span className="w-2 h-2 bg-white/40 rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
                </div>
              </div>
            </div>
          )}

          <div ref={messagesEndRef} />
        </div>

        {/* Input */}
        <form onSubmit={handleSend} className="flex gap-3">
          <input
            ref={inputRef}
            type="text"
            value={input}
            onChange={e => setInput(e.target.value)}
            placeholder="Type your message..."
            disabled={loading}
            className="flex-1 glass rounded-xl px-4 py-3 text-white placeholder-white/40 bg-transparent border-white/10 focus:border-primary-500 focus:ring-1 focus:ring-primary-500 outline-none disabled:opacity-50"
          />
          <button
            type="submit"
            disabled={loading || !input.trim()}
            className="px-4 py-3 bg-primary-600 text-white rounded-xl font-medium hover:bg-primary-700 disabled:opacity-50 transition"
          >
            <FiSend size={18} />
          </button>
        </form>
      </div>
    </div>
  );
}
